import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Play, Pause, Rewind, FastForward, Repeat, Plus, Trash2, Maximize, X, Pencil, Music2, Gauge, Loader2, Check,
  ArrowLeft, Share2, ChevronUp,
} from "lucide-react";
import api, { fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const SPEEDS = [0.5, 0.6, 0.7, 0.8, 0.9, 1, 1.1, 1.2];

const fmt = (s, withMs = false) => {
  if (!isFinite(s) || s == null) return withMs ? "0:00.000" : "0:00";
  const m = Math.floor(s / 60);
  const sec = s % 60;
  if (withMs) return `${m}:${sec.toFixed(3).padStart(6, "0")}`;
  return `${m}:${Math.floor(sec).toString().padStart(2, "0")}`;
};

function MarkerDialog({ open, initial, labels, onSave, onClose, onRenameLabel, onDeleteLabel }) {
  const { t } = useAuth();
  const [seconds, setSeconds] = useState(0);
  const [name, setName] = useState("");
  const [manage, setManage] = useState(false);
  const [editLabel, setEditLabel] = useState(null);
  const [editVal, setEditVal] = useState("");

  useEffect(() => {
    if (open) {
      setSeconds(initial?.seconds ?? 0);
      setName(initial?.label ?? "");
      setManage(false);
      setEditLabel(null);
    }
  }, [open, initial]);

  const step = (d) => setSeconds((s) => Math.max(0, +(s + d).toFixed(3)));

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="card-bg border-app max-w-md z-[300]" overlayClassName="z-[290]" data-testid="marker-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{t(initial?.id ? "mp.marker_edit" : "mp.marker_add_title")}</DialogTitle>
          <DialogDescription className="sr-only">{t("mp.marker_dialog_sr")}</DialogDescription>
        </DialogHeader>
        <p className="text-xs uppercase tracking-wider t-secondary">Timecode</p>
        <div className="flex items-center gap-2">
          <div className="flex-1 text-center text-2xl font-mono font-bold border border-app rounded-lg py-3" data-testid="marker-timecode">
            {fmt(seconds, true)}
          </div>
          <Button
            variant="outline"
            className="border-amber-500/40 text-amber-400 hover:bg-amber-500/10"
            onClick={() => setSeconds(initial?.current ?? seconds)}
            data-testid="marker-current-position"
          >
            {t("mp.current_position")}
          </Button>
        </div>
        <div className="grid grid-cols-4 gap-2">
          {[-1, -0.1, 0.1, 1].map((d) => {
            const tid = { "-1": "minus-1", "-0.1": "minus-0-1", "0.1": "plus-0-1", "1": "plus-1" }[String(d)];
            return (
              <Button key={d} variant="outline" className="border-app" onClick={() => step(d)} data-testid={`marker-step-${tid}`}>
                {d > 0 ? `+${d}s` : `${d}s`}
              </Button>
            );
          })}
        </div>
        <p className="text-xs uppercase tracking-wider t-secondary">{t("usr.col_name")}</p>
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={t("mp.marker_placeholder")} className="bg-transparent border-app" data-testid="marker-label-input" />
        {labels.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs uppercase tracking-wider t-secondary">{t("mp.labels")}</p>
              <button type="button" className="text-xs accent hover:underline" onClick={() => { setManage(!manage); setEditLabel(null); }} data-testid="marker-labels-manage">
                {t(manage ? "mp.manage_done" : "mp.manage")}
              </button>
            </div>
            {manage ? (
              <div className="space-y-1.5 max-h-44 overflow-y-auto" data-testid="marker-labels-manage-list">
                {labels.map((l) => (
                  <div key={l} className="flex items-center gap-2 border border-app rounded-lg px-3 py-1.5">
                    {editLabel === l ? (
                      <>
                        <Input value={editVal} onChange={(e) => setEditVal(e.target.value)} className="bg-transparent border-app h-8 text-sm flex-1" data-testid="marker-label-rename-input" autoFocus />
                        <button
                          type="button"
                          className="p-1.5 accent hover:bg-white/5 rounded"
                          disabled={!editVal.trim()}
                          onClick={() => { onRenameLabel(l, editVal.trim()); setEditLabel(null); }}
                          data-testid="marker-label-rename-confirm"
                          aria-label={t("mp.confirm_rename")}
                        >
                          <Check className="h-4 w-4" />
                        </button>
                      </>
                    ) : (
                      <>
                        <span className="flex-1 text-sm truncate">{l}</span>
                        <button type="button" className="p-1.5 t-secondary hover:text-white rounded" onClick={() => { setEditLabel(l); setEditVal(l); }} data-testid={`marker-label-edit-${l}`} aria-label={t("mp.rename_named", { name: l })}>
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button type="button" className="p-1.5 text-rose-400 hover:text-rose-300 rounded" onClick={() => onDeleteLabel(l)} data-testid={`marker-label-delete-${l}`} aria-label={t("mp.delete_named", { name: l })}>
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-wrap gap-2" data-testid="marker-label-chips">
                {labels.map((l) => (
                  <button
                    key={l}
                    onClick={() => setName(l)}
                    className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                      name === l ? "accent-bg text-slate-900 border-transparent" : "border-app t-secondary hover:bg-white/5"
                    }`}
                    data-testid={`marker-label-chip-${l}`}
                  >
                    {l}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        <div className="flex gap-2 mt-1">
          <Button variant="outline" className="flex-1 border-app" onClick={onClose} data-testid="marker-cancel">{t("common.cancel")}</Button>
          <Button className="flex-1 accent-bg text-slate-900 font-bold hover:opacity-90" onClick={() => name.trim() && onSave({ label: name.trim(), seconds })} disabled={!name.trim()} data-testid="marker-save">
            {t("common.save")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function MediaPlayer({ file, onClose, inline = false, srcOverride = null, downloadOverride = null, features = {}, minimized = false, onMinimize = null, onExpand = null }) {
  // features : { markers, rename, processing } — désactivées hors contexte Groupe (ex. ressources pédagogiques)
  const { markers: markersOn = true, rename: renameOn = true, processing: processingOn = true } = features;
  const { user, t } = useAuth();
  const canEdit = markersOn && user && ["admin", "music_leader"].includes(user.role);
  const mediaRef = useRef(null);
  const wrapRef = useRef(null);
  const hideTimer = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(0);
  const [speed, setSpeed] = useState(1);
  const [loopA, setLoopA] = useState(null);
  const [loopB, setLoopB] = useState(null);
  const [markers, setMarkers] = useState([]);
  const [labels, setLabels] = useState([]);
  const [dlg, setDlg] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [pseudoFs, setPseudoFs] = useState(false); // iOS Safari : pas d'API Fullscreen sur les éléments → plein écran simulé en CSS
  const fsActive = isFullscreen || pseudoFs;
  const fsActiveRef = useRef(false);
  fsActiveRef.current = fsActive;
  const [controlsVisible, setControlsVisible] = useState(true);
  const isVideo = file.kind === "video";
  // Si la version optimisée (transcodée) échoue dans ce navigateur, on retombe sur l'original (?orig=1)
  const [triedOriginal, setTriedOriginal] = useState(false);
  const src = srcOverride || fileUrl(file.id, triedOriginal ? "orig=1" : "");
  const downloadSrc = downloadOverride || fileUrl(file.id, "download=1");
  // Audio sur mobile : page complète dédiée (fond opaque) au lieu de la modale
  const [isMobileAudio] = useState(() => !inline && file.kind !== "video" && window.matchMedia("(max-width: 1023px)").matches);
  // Pas de pré-filtrage canPlayType (trop pessimiste : Chrome répond "" pour video/quicktime
  // alors qu'il lit les MOV/H.264). Le repli « Télécharger » n'apparaît qu'en cas d'erreur réelle.
  const [unsupported, setUnsupported] = useState(false);
  const [procStatus, setProcStatus] = useState(file.processing_status || "ready");
  const [procProgress, setProcProgress] = useState(0);
  const canWrite = ["admin", "music_leader"].includes(user?.role);
  const [renaming, setRenaming] = useState(false);
  const [renameVal, setRenameVal] = useState(file.name);
  const [displayName, setDisplayName] = useState(file.name);
  const saveRename = async () => {
    if (!renameVal.trim()) return;
    try {
      const r = await api.put(`/files/${file.id}`, { name: renameVal.trim() });
      setDisplayName(r.data.name);
      setRenaming(false);
      toast.success(t("docs.file_renamed"));
    } catch (e) { toast.error(e.friendly); }
  };

  // Partage du lien direct vers le morceau dans la bibliothèque (feuille native mobile / copie desktop)
  const shareFile = async () => {
    const url = `${window.location.origin}/bibliotheque?node=${file.node_id}`;
    if (navigator.share) {
      try { await navigator.share({ title: displayName, url }); return; } catch (e) { if (e?.name === "AbortError") return; }
    }
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = url;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      ta.remove();
    }
    toast.success(t("agd.link_copied"));
  };

  // Media Session : contrôles sur l'écran verrouillé / centre de contrôle pendant la lecture en arrière-plan
  useEffect(() => {
    if (!isMobileAudio || !("mediaSession" in navigator)) return;
    try {
      navigator.mediaSession.metadata = new MediaMetadata({ title: displayName, artist: "RANNEA" });
      navigator.mediaSession.setActionHandler("play", () => mediaRef.current?.play());
      navigator.mediaSession.setActionHandler("pause", () => mediaRef.current?.pause());
    } catch { /* non supporté */ }
    return () => {
      try {
        navigator.mediaSession.setActionHandler("play", null);
        navigator.mediaSession.setActionHandler("pause", null);
      } catch { /* ignore */ }
    };
  }, [isMobileAudio, displayName]);

  const loadMarkers = () => {
    if (!markersOn) return;
    api.get(`/files/${file.id}/markers`).then((r) => setMarkers(r.data)).catch(() => {});
    api.get("/marker-labels").then((r) => setLabels(r.data)).catch(() => {});
  };

  useEffect(() => { loadMarkers(); }, [file.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Poll la conversion en cours et recharge la source quand la version optimisée est prête
  useEffect(() => {
    if (!processingOn || procStatus !== "processing") return;
    const t = setInterval(() => {
      api.get(`/files/${file.id}`).then((r) => {
        setProcProgress(r.data.processing_progress || 0);
        if (r.data.processing_status !== "processing") {
          setProcStatus(r.data.processing_status);
          if (r.data.processing_status === "ready") {
            setUnsupported(false);
            if (mediaRef.current) mediaRef.current.load();
          }
          clearInterval(t);
        }
      }).catch(() => {});
    }, 2500);
    return () => clearInterval(t);
  }, [procStatus, file.id]);

  useEffect(() => {
    const onFs = () => setIsFullscreen(!!(document.fullscreenElement || document.webkitFullscreenElement));
    document.addEventListener("fullscreenchange", onFs);
    document.addEventListener("webkitfullscreenchange", onFs);
    return () => {
      document.removeEventListener("fullscreenchange", onFs);
      document.removeEventListener("webkitfullscreenchange", onFs);
    };
  }, []);

  // Pseudo-plein-écran : un ancêtre avec transform/translate/filter… (ex. Dialog Radix) devient
  // le bloc conteneur des descendants position:fixed → neutralisé le temps du pseudoFs
  useEffect(() => {
    if (!pseudoFs || !wrapRef.current) return;
    const DEFAULTS = { transform: "none", translate: "none", scale: "none", rotate: "none", filter: "none", "backdrop-filter": "none", perspective: "none", contain: "none", "will-change": "auto" };
    const altered = [];
    let el = wrapRef.current.parentElement;
    while (el && el !== document.body) {
      const cs = getComputedStyle(el);
      const dirtyProps = Object.keys(DEFAULTS).filter((p) => cs.getPropertyValue(p) !== DEFAULTS[p]);
      if (dirtyProps.length) {
        altered.push([el, el.style.cssText]);
        dirtyProps.forEach((p) => el.style.setProperty(p, DEFAULTS[p], "important"));
      }
      el = el.parentElement;
    }
    return () => altered.forEach(([n, css]) => { n.style.cssText = css; });
  }, [pseudoFs]);

  // Masquage auto des contrôles plein écran : 4 s sur tactile, 5 s au pointeur fin
  const HIDE_MS = window.matchMedia?.("(pointer: coarse)").matches ? 4000 : 5000;
  const pokeControls = () => {
    setControlsVisible(true);
    clearTimeout(hideTimer.current);
    hideTimer.current = setTimeout(() => {
      if (fsActiveRef.current) setControlsVisible(false);
    }, HIDE_MS);
  };

  const onMediaError = async () => {
    // Hors contexte Groupe : pas de version transcodée → repli direct sur « Télécharger »
    if (!processingOn) {
      setUnsupported(true);
      return;
    }
    // Si le serveur convertit encore, ne pas afficher le repli : le poll rechargera la version optimisée.
    let doc = null;
    try {
      const r = await api.get(`/files/${file.id}`);
      doc = r.data;
      if (doc.processing_status === "processing") {
        setProcStatus("processing");
        return;
      }
    } catch { /* ignorer */ }
    // Version optimisée illisible ici → tenter l'original une fois
    if (!triedOriginal && (doc?.has_playback || doc?.playback_mime)) {
      setTriedOriginal(true);
      return;
    }
    setUnsupported(true);
  };
  const retryMedia = () => {
    setUnsupported(false);
    if (mediaRef.current) mediaRef.current.load();
  };

  // Quand on bascule sur l'original, forcer le rechargement de la source
  useEffect(() => {
    if (triedOriginal && mediaRef.current) mediaRef.current.load();
  }, [triedOriginal]);

  const m = () => mediaRef.current;
  const toggle = () => { if (!m()) return; if (m().paused) m().play(); else m().pause(); };
  const seek = (v) => { if (!m()) return; m().currentTime = v; setCurrent(v); };

  const changeSpeed = (v) => {
    const clamped = Math.min(1.2, Math.max(0.5, Math.round(v * 10) / 10));
    setSpeed(clamped);
    if (m()) {
      m().playbackRate = clamped;
      m().preservesPitch = true;
    }
  };
  const stepSpeed = (dir) => {
    const i = SPEEDS.reduce((best, s, idx) => (Math.abs(s - speed) < Math.abs(SPEEDS[best] - speed) ? idx : best), 0);
    changeSpeed(SPEEDS[Math.min(SPEEDS.length - 1, Math.max(0, i + dir))]);
  };

  const onTimeUpdate = () => {
    if (!m()) return;
    const t = m().currentTime;
    if (loopA != null && loopB != null && t >= loopB) {
      m().currentTime = loopA;
      setCurrent(loopA);
      return;
    }
    setCurrent(t);
  };

  const setA = () => {
    setLoopA(current);
    if (loopB != null && loopB <= current) setLoopB(null);
  };
  const setB = () => {
    if (loopA != null && current > loopA) setLoopB(current);
    else toast.error(t("mp.ab_error"));
  };
  const clearLoop = () => { setLoopA(null); setLoopB(null); };

  const saveMarker = async ({ label, seconds }) => {
    try {
      if (dlg?.id) await api.put(`/markers/${dlg.id}`, { label, seconds });
      else await api.post(`/files/${file.id}/markers`, { label, seconds });
      toast.success(t(dlg?.id ? "mp.marker_updated" : "mp.marker_added"));
      setDlg(null);
      loadMarkers();
    } catch (e) { toast.error(e.friendly); }
  };

  const removeMarker = async (id) => {
    await api.delete(`/markers/${id}`).catch(() => {});
    setMarkers((prev) => prev.filter((mk) => mk.id !== id));
  };

  const renameLabel = async (oldL, newL) => {
    try {
      await api.put("/marker-labels", { old_label: oldL, new_label: newL });
      loadMarkers();
      toast.success(t("mp.label_renamed"));
    } catch (e) { toast.error(e.friendly); }
  };

  const deleteLabel = async (l) => {
    try {
      await api.delete(`/marker-labels/${encodeURIComponent(l)}`);
      loadMarkers();
      toast.success(t("mp.label_deleted"));
    } catch (e) { toast.error(e.friendly); }
  };

  const fullscreen = async () => {
    const el = wrapRef.current;
    if (!el) return;
    if (pseudoFs) { setPseudoFs(false); return; }
    try {
      if (document.fullscreenElement || document.webkitFullscreenElement) {
        if (document.exitFullscreen) await document.exitFullscreen();
        else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
        return;
      }
      const req = el.requestFullscreen || el.webkitRequestFullscreen;
      if (req) await req.call(el);
      else setPseudoFs(true);
    } catch {
      setPseudoFs(true);
    }
  };

  const pct = (s) => `${(Math.min(s, duration || 0) / (duration || 1)) * 100}%`;
  const currentMarker = [...markers].reverse().find((mk) => mk.seconds <= current + 0.05);

  const header = inline ? (
    <div className="flex items-center justify-between gap-2 mb-2">
      {renaming ? (
        <span className="flex items-center gap-2 flex-1 min-w-0">
          <Input value={renameVal} onChange={(e) => setRenameVal(e.target.value)} className="h-8 bg-transparent border-app" data-testid="media-rename-input" autoFocus onKeyDown={(e) => e.key === "Enter" && saveRename()} />
          <Button size="sm" className="brand-bg text-white h-8 shrink-0" onClick={saveRename} data-testid="media-rename-save">OK</Button>
        </span>
      ) : (
        <h3 className="font-heading font-bold truncate">{displayName}</h3>
      )}
      <span className="flex items-center gap-1 shrink-0">
        {canWrite && renameOn && !renaming && (
          <button onClick={() => { setRenameVal(displayName); setRenaming(true); }} className="t-secondary hover:text-white" data-testid="media-rename-button" title={t("common.rename")}>
            <Pencil className="h-4 w-4" />
          </button>
        )}
        <button onClick={onClose} className="t-secondary hover:text-white shrink-0" data-testid="media-player-inline-close">
          <X className="h-4 w-4" />
        </button>
      </span>
    </div>
  ) : (
    <DialogHeader>
      <DialogTitle className="font-heading truncate flex items-center gap-2">
        {renaming ? (
          <span className="flex items-center gap-2 flex-1 min-w-0">
            <Input value={renameVal} onChange={(e) => setRenameVal(e.target.value)} className="h-8 bg-transparent border-app" data-testid="media-rename-input" autoFocus onKeyDown={(e) => e.key === "Enter" && saveRename()} />
            <Button size="sm" className="brand-bg text-white h-8 shrink-0" onClick={saveRename} data-testid="media-rename-save">OK</Button>
          </span>
        ) : (
          <span className="truncate">{displayName}</span>
        )}
        {canWrite && renameOn && !renaming && (
          <button onClick={() => { setRenameVal(displayName); setRenaming(true); }} className="t-secondary hover:text-white shrink-0" data-testid="media-rename-button" title={t("common.rename")}>
            <Pencil className="h-4 w-4" />
          </button>
        )}
      </DialogTitle>
      <DialogDescription className="sr-only">{t("mp.player_sr")}</DialogDescription>
    </DialogHeader>
  );

  // Barre de progression discrète de la conversion (partagée desktop / mobile)
  const processingBanner = (extraCls = "") => (
    <div className={extraCls} data-testid="media-processing-banner">
      <p className="text-xs accent flex items-center gap-2">
        <Loader2 className="h-3.5 w-3.5 animate-spin" /> {t("mp.processing")}
        <span className="t-secondary ml-auto tabular-nums" data-testid="media-processing-pct">{procProgress} %</span>
      </p>
      <div className="h-1 mt-1.5 rounded-full bg-white/10 overflow-hidden" data-testid="media-processing-bar">
        <div className="h-full brand-bg transition-[width] duration-500" style={{ width: `${procProgress}%` }} />
      </div>
    </div>
  );

  const content = (
    <>
      {header}
      {processingOn && procStatus === "processing" && processingBanner("mb-2")}
      <div
        ref={wrapRef}
        className={pseudoFs ? "fixed inset-0 z-[200] bg-black flex flex-col justify-center" : `relative ${isVideo ? "bg-black rounded-lg overflow-hidden" : ""}`}
        onMouseMove={pokeControls}
        onTouchStart={pokeControls}
      >
        {unsupported ? (
          <div className="text-center py-10 space-y-4" data-testid="media-unsupported">
            <Music2 className="h-10 w-10 t-secondary mx-auto" />
            <p className="text-sm t-secondary">
              {t("mp.unsupported", { ext: (file.name || "").split(".").pop()?.toUpperCase() })}
            </p>
            <div className="flex items-center justify-center gap-3">
              <button onClick={retryMedia} className="px-4 py-2 rounded-lg border border-app text-sm hover:bg-white/5" data-testid="media-retry-button">{t("mp.retry")}</button>
              <a href={downloadSrc} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg brand-bg text-white text-sm font-semibold" data-testid="media-download-button">{t("mp.download")}</a>
            </div>
          </div>
        ) : isVideo ? (
          <video
            ref={mediaRef}
            src={src}
            playsInline
            webkit-playsinline="true"
            className={`w-full object-contain ${fsActive ? "h-full max-h-none" : "max-h-[45vh]"}`}
            onTimeUpdate={onTimeUpdate}
            onLoadedMetadata={() => setDuration(m()?.duration || 0)}
            onPlay={() => setPlaying(true)}
            onPause={() => setPlaying(false)}
            onError={onMediaError}
            onClick={toggle}
            data-testid="media-player-video"
          />
        ) : (
          <>
            <div className="flex justify-center py-3">
              <div className="h-16 w-16 rounded-2xl accent-bg/20 flex items-center justify-center">
                <Music2 className="h-8 w-8 accent" />
              </div>
            </div>
            <audio
              ref={mediaRef}
              src={src}
              onTimeUpdate={onTimeUpdate}
              onLoadedMetadata={() => setDuration(m()?.duration || 0)}
              onPlay={() => setPlaying(true)}
              onPause={() => setPlaying(false)}
              onError={onMediaError}
              data-testid="media-player-audio"
            />
          </>
        )}
        <div
          className={`${unsupported ? "hidden" : fsActive && !controlsVisible ? "opacity-0 pointer-events-none invisible" : "opacity-100 visible"} ${isVideo ? "pt-3" : ""} ${fsActive ? "absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/95 via-black/70 to-transparent p-4 max-h-[60vh] overflow-y-auto" : ""}`}
          style={{ transition: "opacity 0.3s ease, visibility 0.3s ease", willChange: "opacity" }}
        >
          {fsActive ? (
            <>
              {/* Rangée 1 : timeline pleine largeur avec repères intégrés */}
              <div className="relative flex items-center h-8" data-testid="media-progress-zone">
                {loopA != null && loopB != null && duration > 0 && (
                  <div className="absolute top-1/2 -translate-y-1/2 h-2 accent-bg/40 rounded-full pointer-events-none" style={{ left: pct(loopA), width: `calc(${pct(loopB)} - ${pct(loopA)})` }} data-testid="ab-loop-band" />
                )}
                <input
                  type="range"
                  min={0}
                  max={duration || 0}
                  step={0.05}
                  value={current}
                  onChange={(e) => seek(parseFloat(e.target.value))}
                  className="w-full accent-emerald-500 relative z-10"
                  data-testid="media-progress-bar"
                />
                {duration > 0 && markers.map((mk) => (
                  <button
                    key={mk.id}
                    className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 h-5 w-1.5 bg-white/80 rounded-full z-20 hover:bg-amber-400 transition-colors"
                    style={{ left: pct(mk.seconds) }}
                    onClick={() => seek(mk.seconds)}
                    title={`${fmt(mk.seconds)} · ${mk.label}`}
                    data-testid={`marker-tick-${mk.id}`}
                  />
                ))}
              </div>
              {/* Rangée 2 : transport + temps | boucle A-B + quitter plein écran (wrap sur mobile) */}
              <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 mb-2">
                <div className="flex items-center gap-2">
                  <Button size="icon" variant="outline" className="border-app rounded-full h-9 w-9" onClick={() => seek(Math.max(0, current - 10))} data-testid="media-rewind-button">
                    <Rewind className="h-4 w-4" />
                  </Button>
                  <Button size="icon" className="brand-bg text-white rounded-full h-12 w-12" onClick={toggle} data-testid="media-play-pause-button">
                    {playing ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5 ml-0.5" />}
                  </Button>
                  <Button size="icon" variant="outline" className="border-app rounded-full h-9 w-9" onClick={() => seek(Math.min(duration, current + 10))} data-testid="media-forward-button">
                    <FastForward className="h-4 w-4" />
                  </Button>
                  <span className="text-xs t-secondary ml-2 whitespace-nowrap">
                    <span data-testid="media-current-time">{fmt(current)}</span> / <span data-testid="media-duration">{fmt(duration)}</span>
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={setA}
                    className={`px-3 py-2 rounded-lg border text-sm font-bold transition-colors ${loopA != null ? "border-amber-500/40 text-amber-400" : "border-app t-secondary hover:bg-white/5"}`}
                    data-testid="ab-loop-set-a-button"
                  >
                    A
                  </button>
                  <button
                    onClick={setB}
                    className={`px-3 py-2 rounded-lg border text-sm font-bold transition-colors ${loopB != null ? "border-amber-500/40 text-amber-400" : "border-app t-secondary hover:bg-white/5"}`}
                    data-testid="ab-loop-set-b-button"
                  >
                    B
                  </button>
                  <button
                    onClick={clearLoop}
                    className={`p-2 rounded-lg border transition-colors ${loopA != null ? "border-amber-500/40 text-amber-400" : "border-app t-secondary"}`}
                    title={t("mp.clear_loop")}
                    data-testid="ab-loop-clear-button"
                  >
                    <Repeat className="h-4 w-4" />
                  </button>
                  <Button size="icon" variant="outline" className="border-app rounded-lg" onClick={fullscreen} data-testid="media-fullscreen-button" title={t("mp.exit_fullscreen")}>
                    <Maximize className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              {/* Rangée 3 : repères en chips | vitesse */}
              <div className="flex items-end justify-between gap-4">
                <div className="flex flex-wrap gap-1.5 overflow-y-auto max-h-24" data-testid="markers-chips">
                  {markers.map((mk) => (
                    <button
                      key={mk.id}
                      onClick={() => seek(mk.seconds)}
                      className="px-2.5 py-1 rounded-full bg-black/50 border border-app/60 text-xs hover:border-amber-500/50 transition-colors"
                      data-testid={`marker-chip-${mk.id}`}
                    >
                      <span className="accent font-mono mr-1.5">{fmt(mk.seconds)}</span><span>{mk.label}</span>
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-2 shrink-0" data-testid="media-speed-card">
                  <Gauge className="h-3.5 w-3.5 t-secondary" />
                  <span className="text-xs font-bold accent w-11 text-right" data-testid="media-speed-value">{speed.toFixed(2)}x</span>
                  <button className="px-2 py-1 rounded border border-app/60 text-sm t-secondary hover:bg-white/10" onClick={() => stepSpeed(-1)} data-testid="media-speed-minus">−</button>
                  <input
                    type="range"
                    min={0.5}
                    max={1.2}
                    step={0.1}
                    value={speed}
                    onChange={(e) => changeSpeed(parseFloat(e.target.value))}
                    className="w-28 accent-amber-500 h-1"
                    data-testid="media-speed-slider"
                  />
                  <button className="px-2 py-1 rounded border border-app/60 text-sm t-secondary hover:bg-white/10" onClick={() => stepSpeed(1)} data-testid="media-speed-plus">+</button>
                </div>
              </div>
            </>
          ) : (
            <>
          {/* Transport */}
          <div className="flex items-center justify-center gap-2 mb-2">
            <Button size="icon" variant="outline" className="border-app rounded-full" onClick={() => seek(Math.max(0, current - 10))} data-testid="media-rewind-button">
              <Rewind className="h-4 w-4" />
            </Button>
            <Button size="icon" className="brand-bg text-white rounded-full h-11 w-11" onClick={toggle} data-testid="media-play-pause-button">
              {playing ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5 ml-0.5" />}
            </Button>
            <Button size="icon" variant="outline" className="border-app rounded-full" onClick={() => seek(Math.min(duration, current + 10))} data-testid="media-forward-button">
              <FastForward className="h-4 w-4" />
            </Button>
            {isVideo && (
              <Button size="icon" variant="outline" className="border-app rounded-full" onClick={fullscreen} data-testid="media-fullscreen-button">
                <Maximize className="h-4 w-4" />
              </Button>
            )}
          </div>

          {/* Barre de progression avec repères intégrés */}
          <div className="relative flex items-center h-8" data-testid="media-progress-zone">
            {loopA != null && loopB != null && duration > 0 && (
              <div className="absolute top-1/2 -translate-y-1/2 h-2 accent-bg/40 rounded-full pointer-events-none" style={{ left: pct(loopA), width: `calc(${pct(loopB)} - ${pct(loopA)})` }} data-testid="ab-loop-band" />
            )}
            <input
              type="range"
              min={0}
              max={duration || 0}
              step={0.05}
              value={current}
              onChange={(e) => seek(parseFloat(e.target.value))}
              className="w-full accent-emerald-500 relative z-10"
              data-testid="media-progress-bar"
            />
            {duration > 0 && markers.map((mk) => (
              <button
                key={mk.id}
                className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 h-5 w-1.5 bg-white/80 rounded-full z-20 hover:bg-amber-400 transition-colors"
                style={{ left: pct(mk.seconds) }}
                onClick={() => seek(mk.seconds)}
                title={`${fmt(mk.seconds)} · ${mk.label}`}
                data-testid={`marker-tick-${mk.id}`}
              />
            ))}
          </div>
          <div className="flex justify-between text-xs t-secondary -mt-1 mb-2">
            <span data-testid="media-current-time">{fmt(current)}</span>
            <span data-testid="media-duration">{fmt(duration)}</span>
          </div>

          {/* Vitesse : barre de réglage − / + (compacte en plein écran) */}
          {fsActive ? (
            <div className="flex items-center gap-2 max-w-md mx-auto mb-2" data-testid="media-speed-card">
              <Gauge className="h-3.5 w-3.5 t-secondary shrink-0" />
              <button className="px-2 py-1 rounded border border-app/60 text-sm t-secondary hover:bg-white/10" onClick={() => stepSpeed(-1)} data-testid="media-speed-minus">−</button>
              <input
                type="range"
                min={0.5}
                max={1.2}
                step={0.1}
                value={speed}
                onChange={(e) => changeSpeed(parseFloat(e.target.value))}
                className="flex-1 accent-amber-500 h-1"
                data-testid="media-speed-slider"
              />
              <button className="px-2 py-1 rounded border border-app/60 text-sm t-secondary hover:bg-white/10" onClick={() => stepSpeed(1)} data-testid="media-speed-plus">+</button>
              <span className="text-xs font-bold accent w-12 text-right shrink-0" data-testid="media-speed-value">{speed.toFixed(2)}x</span>
            </div>
          ) : (
          <div className="border border-app rounded-xl p-3 mb-3" data-testid="media-speed-card">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-sm t-secondary flex items-center gap-2"><Gauge className="h-4 w-4" /> {t("mp.speed")}</span>
              <span className="text-sm font-bold accent" data-testid="media-speed-value">{speed.toFixed(2)}x</span>
            </div>
            <div className="flex items-center gap-3">
              <Button size="icon" variant="outline" className="border-app rounded-lg shrink-0" onClick={() => stepSpeed(-1)} data-testid="media-speed-minus">−</Button>
              <input
                type="range"
                min={0.5}
                max={1.2}
                step={0.1}
                value={speed}
                onChange={(e) => changeSpeed(parseFloat(e.target.value))}
                className="flex-1 accent-amber-500"
                data-testid="media-speed-slider"
              />
              <Button size="icon" variant="outline" className="border-app rounded-lg shrink-0" onClick={() => stepSpeed(1)} data-testid="media-speed-plus">+</Button>
            </div>
          </div>
          )}

          {/* Boucle A-B */}
          <div className="flex items-center gap-2 flex-wrap mb-3">
            <button
              onClick={clearLoop}
              className={`p-2 rounded-lg border transition-colors ${loopA != null ? "border-amber-500/40 text-amber-400" : "border-app t-secondary"}`}
              title={t("mp.clear_loop")}
              data-testid="ab-loop-clear-button"
            >
              <Repeat className="h-4 w-4" />
            </button>
            <button
              onClick={setA}
              className={`px-4 py-2 rounded-lg border text-sm font-bold transition-colors ${loopA != null ? "border-amber-500/40 text-amber-400" : "border-app t-secondary hover:bg-white/5"}`}
              data-testid="ab-loop-set-a-button"
            >
              A&nbsp;&nbsp;{loopA != null ? fmt(loopA) : "--:--"}
            </button>
            <button
              onClick={setB}
              className={`px-4 py-2 rounded-lg border text-sm font-bold transition-colors ${loopB != null ? "border-amber-500/40 text-amber-400" : "border-app t-secondary hover:bg-white/5"}`}
              data-testid="ab-loop-set-b-button"
            >
              B&nbsp;&nbsp;{loopB != null ? fmt(loopB) : "--:--"}
            </button>
          </div>

          {/* Lecture en cours */}
          {currentMarker && (
            <p className="text-sm mb-3" data-testid="now-playing-marker">
              <span className="inline-block h-2 w-2 rounded-full accent-bg mr-2 align-middle" />
              <span className="t-secondary uppercase tracking-wider text-xs">{t("mp.now_playing")}&nbsp;&nbsp;</span>
              <span className="accent font-semibold">{currentMarker.label}</span>
            </p>
          )}

          {/* Repères */}
          {markersOn && (
          <div className={`mt-3 border-t border-app pt-3 ${fsActive ? "max-h-40 overflow-y-auto" : ""}`}>
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs font-semibold uppercase tracking-wider t-secondary">{t("mp.markers")}</p>
          {canEdit && (
            <Button size="sm" className="accent-bg text-slate-900 font-semibold" onClick={() => setDlg({ seconds: current, current, label: "" })} data-testid="marker-add-button">
              <Plus className="h-4 w-4 mr-1" /> {t("mp.marker_add")}
            </Button>
          )}
        </div>
        <div className="space-y-1" data-testid="markers-list">
          {markers.length === 0 && <p className="text-xs t-secondary py-2">{t("mp.no_markers")}</p>}
          {markers.map((mk) => (
            <div key={mk.id} className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-white/5" data-testid={`marker-row-${mk.id}`}>
              <button className="text-xs font-mono accent w-12 text-left" onClick={() => seek(mk.seconds)} data-testid={`marker-seek-${mk.id}`}>
                {fmt(mk.seconds)}
              </button>
              <span className="flex-1 text-sm truncate">{mk.label}</span>
              {canEdit && (
                <>
                  <button onClick={() => setDlg({ id: mk.id, seconds: mk.seconds, current, label: mk.label })} className="t-secondary hover:text-white" data-testid={`marker-edit-${mk.id}`}>
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                  <button onClick={() => removeMarker(mk.id)} className="t-secondary hover:text-rose-400" data-testid={`marker-delete-${mk.id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </>
              )}
            </div>
          ))}
        </div>
          </div>
          )}
            </>
          )}
        </div>
      </div>

      <MarkerDialog
        open={!!dlg}
        initial={dlg}
        labels={labels}
        onSave={saveMarker}
        onClose={() => setDlg(null)}
        onRenameLabel={renameLabel}
        onDeleteLabel={deleteLabel}
      />
    </>
  );

  // Page lecteur audio mobile plein écran (maquette utilisateur) : fond opaque, pochette,
  // BPM de la fiche morceau, timeline, transport, boucle A-B, vitesse, parties (repères)
  if (isMobileAudio) {
    const audioEl = (
      <audio
        ref={mediaRef}
        src={src}
        onTimeUpdate={onTimeUpdate}
        onLoadedMetadata={() => setDuration(m()?.duration || 0)}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onError={onMediaError}
        data-testid="media-player-audio"
      />
    );
    // Mini-barre persistante : l'audio continue, tout est contrôlable sans rouvrir la page
    if (minimized) {
      return (
        <>
          {audioEl}
          <div className="fixed bottom-16 left-2 right-2 z-40 card-bg border border-app rounded-xl shadow-xl px-3 py-2 flex items-center gap-2" data-testid="mini-player-bar">
            <Music2 className="h-5 w-5 accent shrink-0" />
            <button className="flex-1 min-w-0 text-left" onClick={onExpand || undefined} data-testid="mini-player-expand">
              <span className="block text-sm font-semibold truncate">{displayName}</span>
              <span className="block text-[11px] t-secondary font-mono">{fmt(current)} / {fmt(duration)}</span>
            </button>
            <div className="h-1 w-14 rounded-full bg-white/10 overflow-hidden shrink-0 hidden min-[360px]:block" data-testid="mini-player-progress">
              <div className="h-full accent-bg transition-[width]" style={{ width: duration ? `${(current / duration) * 100}%` : "0%" }} />
            </div>
            <Button size="icon" className="accent-bg text-slate-900 rounded-full h-9 w-9 shrink-0" onClick={toggle} data-testid="mini-player-toggle">
              {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4 ml-0.5" />}
            </Button>
            <button onClick={onExpand || undefined} className="p-1.5 t-secondary hover:text-white shrink-0" data-testid="mini-player-expand-btn" aria-label={t("mp.expand")}>
              <ChevronUp className="h-5 w-5" />
            </button>
            <button onClick={onClose} className="p-1.5 t-secondary hover:text-rose-400 shrink-0" data-testid="mini-player-close" aria-label={t("mp.stop")}>
              <X className="h-4 w-4" />
            </button>
          </div>
        </>
      );
    }
    return (
      <>
      {audioEl}
      <div className="fixed inset-0 z-[200] app-bg overflow-y-auto" data-testid="media-mobile-page">
        <div className="sticky top-0 z-10 flex items-center justify-between px-3 py-3 app-bg/95 backdrop-blur border-b border-app/40">
          <button onClick={onMinimize || onClose} className="p-2 -ml-1 t-secondary hover:text-white transition-colors" data-testid="media-mobile-back" aria-label={t("common.back")}>
            <ArrowLeft className="h-6 w-6" />
          </button>
          <h2 className="font-heading text-lg font-bold">{t("mp.audio_player")}</h2>
          <button onClick={shareFile} className="p-2 -mr-1 t-secondary hover:text-white transition-colors" data-testid="media-mobile-share" aria-label={t("mp.share")}>
            <Share2 className="h-5 w-5" />
          </button>
        </div>
        <div className="px-5 pb-10 max-w-md mx-auto">
          {processingOn && procStatus === "processing" && processingBanner("mt-3")}
          {unsupported ? (
            <div className="text-center py-10 space-y-4" data-testid="media-unsupported">
              <Music2 className="h-10 w-10 t-secondary mx-auto" />
              <p className="text-sm t-secondary">{t("mp.unsupported", { ext: (file.name || "").split(".").pop()?.toUpperCase() })}</p>
              <div className="flex items-center justify-center gap-3">
                <button onClick={retryMedia} className="px-4 py-2 rounded-lg border border-app text-sm hover:bg-white/5" data-testid="media-retry-button">{t("mp.retry")}</button>
                <a href={downloadSrc} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg brand-bg text-white text-sm font-semibold" data-testid="media-download-button">{t("mp.download")}</a>
              </div>
            </div>
          ) : (
            <>
              <div className="mx-auto mt-6 h-44 w-44 rounded-3xl bg-white/5 flex items-center justify-center" data-testid="media-mobile-cover">
                <Music2 className="h-16 w-16 accent" />
              </div>
              <h3 className="mt-5 text-center font-heading text-xl font-extrabold uppercase leading-snug break-words" data-testid="media-mobile-name">{displayName}</h3>
              {file.pieceTempo ? (
                <p className="mt-3 flex justify-center">
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-500/15 text-amber-400 text-xs font-bold" data-testid="media-bpm-badge">
                    <Gauge className="h-3.5 w-3.5" /> {file.pieceTempo} BPM
                  </span>
                </p>
              ) : null}

              {/* Timeline + repères */}
              <div className="relative flex items-center h-8 mt-5" data-testid="media-progress-zone">
                {loopA != null && loopB != null && duration > 0 && (
                  <div className="absolute top-1/2 -translate-y-1/2 h-2 accent-bg/40 rounded-full pointer-events-none" style={{ left: pct(loopA), width: `calc(${pct(loopB)} - ${pct(loopA)})` }} data-testid="ab-loop-band" />
                )}
                <input
                  type="range"
                  min={0}
                  max={duration || 0}
                  step={0.05}
                  value={current}
                  onChange={(e) => seek(parseFloat(e.target.value))}
                  className="w-full accent-emerald-500 relative z-10"
                  data-testid="media-progress-bar"
                />
                {duration > 0 && markers.map((mk) => (
                  <button
                    key={mk.id}
                    className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 h-5 w-1.5 bg-white/80 rounded-full z-20 hover:bg-amber-400 transition-colors"
                    style={{ left: pct(mk.seconds) }}
                    onClick={() => seek(mk.seconds)}
                    title={`${fmt(mk.seconds)} · ${mk.label}`}
                    data-testid={`marker-tick-${mk.id}`}
                  />
                ))}
              </div>
              <div className="flex justify-between text-xs t-secondary -mt-1 mb-4">
                <span data-testid="media-current-time">{fmt(current)}</span>
                <span data-testid="media-duration">{fmt(duration)}</span>
              </div>

              {/* Transport */}
              <div className="flex items-center justify-center gap-5 mb-4">
                <Button size="icon" variant="outline" className="border-app rounded-full h-11 w-11" onClick={() => seek(Math.max(0, current - 10))} data-testid="media-rewind-button">
                  <Rewind className="h-5 w-5" />
                </Button>
                <Button size="icon" className="accent-bg text-slate-900 rounded-full h-16 w-16 hover:opacity-90" onClick={toggle} data-testid="media-play-pause-button">
                  {playing ? <Pause className="h-7 w-7" /> : <Play className="h-7 w-7 ml-1" />}
                </Button>
                <Button size="icon" variant="outline" className="border-app rounded-full h-11 w-11" onClick={() => seek(Math.min(duration, current + 10))} data-testid="media-forward-button">
                  <FastForward className="h-5 w-5" />
                </Button>
              </div>

              {/* Boucle A-B */}
              <div className="flex items-center justify-center gap-2 flex-wrap mb-4">
                <button
                  onClick={clearLoop}
                  className={`px-4 py-2 rounded-full border text-sm font-semibold transition-colors flex items-center gap-1.5 ${loopA != null ? "border-amber-500/40 text-amber-400" : "border-app t-secondary hover:bg-white/5"}`}
                  data-testid="ab-loop-clear-button"
                >
                  <Repeat className="h-4 w-4" /> {t("mp.loop")}
                </button>
                <button
                  onClick={setA}
                  className={`px-4 py-2 rounded-full border text-sm font-bold transition-colors ${loopA != null ? "border-amber-500/40 text-amber-400" : "border-app t-secondary hover:bg-white/5"}`}
                  data-testid="ab-loop-set-a-button"
                >
                  A&nbsp;&nbsp;{loopA != null ? fmt(loopA) : "--:--"}
                </button>
                <button
                  onClick={setB}
                  className={`px-4 py-2 rounded-full border text-sm font-bold transition-colors ${loopB != null ? "border-amber-500/40 text-amber-400" : "border-app t-secondary hover:bg-white/5"}`}
                  data-testid="ab-loop-set-b-button"
                >
                  B&nbsp;&nbsp;{loopB != null ? fmt(loopB) : "--:--"}
                </button>
              </div>

              {/* Vitesse */}
              <div className="border border-app rounded-xl p-3 mb-4" data-testid="media-speed-card">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-sm t-secondary flex items-center gap-2"><Gauge className="h-4 w-4" /> {t("mp.speed")}</span>
                  <span className="text-sm font-bold accent" data-testid="media-speed-value">{speed.toFixed(2)}x</span>
                </div>
                <div className="flex items-center gap-3">
                  <Button size="icon" variant="outline" className="border-app rounded-lg shrink-0" onClick={() => stepSpeed(-1)} data-testid="media-speed-minus">−</Button>
                  <input
                    type="range"
                    min={0.5}
                    max={1.2}
                    step={0.1}
                    value={speed}
                    onChange={(e) => changeSpeed(parseFloat(e.target.value))}
                    className="flex-1 accent-amber-500"
                    data-testid="media-speed-slider"
                  />
                  <Button size="icon" variant="outline" className="border-app rounded-lg shrink-0" onClick={() => stepSpeed(1)} data-testid="media-speed-plus">+</Button>
                </div>
              </div>

              {/* Lecture en cours */}
              {currentMarker && (
                <p className="text-sm mb-4" data-testid="now-playing-marker">
                  <span className="inline-block h-2 w-2 rounded-full accent-bg mr-2 align-middle" />
                  <span className="t-secondary uppercase tracking-wider text-xs">{t("mp.now_playing")}&nbsp;&nbsp;</span>
                  <span className="accent font-semibold">{currentMarker.label}</span>
                </p>
              )}

              {/* Parties (repères) */}
              {markersOn && (
                <div className="border-t border-app pt-3">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-semibold uppercase tracking-wider t-secondary">{t("mp.parties")}</p>
                    {canEdit && (
                      <Button size="sm" className="accent-bg text-slate-900 font-semibold rounded-full" onClick={() => setDlg({ seconds: current, current, label: "" })} data-testid="marker-add-button">
                        <Plus className="h-4 w-4 mr-1" /> {t("mp.marker_add")}
                      </Button>
                    )}
                  </div>
                  <div className="space-y-1" data-testid="markers-list">
                    {markers.length === 0 && <p className="text-xs t-secondary py-2">{t("mp.no_markers")}</p>}
                    {markers.map((mk) => (
                      <div key={mk.id} className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-white/5" data-testid={`marker-row-${mk.id}`}>
                        <button className="text-xs font-mono accent w-12 text-left" onClick={() => seek(mk.seconds)} data-testid={`marker-seek-${mk.id}`}>
                          {fmt(mk.seconds)}
                        </button>
                        <span className="flex-1 text-sm truncate">{mk.label}</span>
                        {canEdit && (
                          <>
                            <button onClick={() => setDlg({ id: mk.id, seconds: mk.seconds, current, label: mk.label })} className="t-secondary hover:text-white" data-testid={`marker-edit-${mk.id}`}>
                              <Pencil className="h-3.5 w-3.5" />
                            </button>
                            <button onClick={() => removeMarker(mk.id)} className="t-secondary hover:text-rose-400" data-testid={`marker-delete-${mk.id}`}>
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
        <MarkerDialog
          open={!!dlg}
          initial={dlg}
          labels={labels}
          onSave={saveMarker}
          onClose={() => setDlg(null)}
          onRenameLabel={renameLabel}
          onDeleteLabel={deleteLabel}
        />
      </div>
      </>
    );
  }

  if (inline) {
    return (
      <div className="flex flex-col h-full" data-testid="media-player-inline">{content}</div>
    );
  }

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className={`card-bg border-app max-h-[90vh] overflow-y-auto ${isVideo ? "max-w-4xl" : "max-w-2xl"}`} data-testid="media-player-dialog">
        {content}
      </DialogContent>
    </Dialog>
  );
}
